import { ReactNode, createContext, useRef, useState } from 'react'
import { PortalMessageOptions } from '../types'
import { messagePropsFromMessageOptions } from '../utilities'
import PortalMessage from './'

const DEFAULT_DELAY = 5000

interface QueueMessage extends PortalMessageOptions {
    content: ReactNode
}

export type PortalMessageContextProps = {
    showMessage: (message: ReactNode, options?: PortalMessageOptions) => void
}

export const PortalMessageContext = createContext({} as PortalMessageContextProps)

export const PortalMessageContextManager = ({ children }: { children: ReactNode }) => {
    const delayRef = useRef<number | null>(null)
    const queueRef = useRef<QueueMessage[]>([])

    const [message, setMessage] = useState<QueueMessage | null>(null)
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const messageProps = message ? messagePropsFromMessageOptions(message) : {}

    function nextMessage() {
        if (isOpen) return

        const displayMessage = queueRef.current[0]

        if (delayRef.current) {
            clearTimeout(delayRef.current)
        }

        if (!displayMessage) {
            setMessage(null)
            return
        }

        const { delay = DEFAULT_DELAY, persistent = false } = displayMessage

        setMessage(displayMessage)
        setIsOpen(true)

        if (!persistent) {
            delayRef.current = window?.setTimeout(() => {
                setIsOpen(false)
            }, delay)
        }
    }

    function removeMessage() {
        queueRef.current = queueRef.current.slice(1)
        nextMessage()
    }

    function showMessage(message: ReactNode, options: PortalMessageOptions = {}) {
        queueRef.current.push({ ...options, content: message })
        nextMessage()
    }

    return (
        <PortalMessageContext.Provider value={{ showMessage }}>
            {children}
            {message && (
                <PortalMessage
                    {...messageProps}
                    closeable
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onCloseEnd={() => removeMessage()}
                >
                    {message.content}
                </PortalMessage>
            )}
        </PortalMessageContext.Provider>
    )
}
